<template>
  <div class="mb-4 pa-4">
    <v-card
      v-for="(future) in futures"
      :key="future.name"
      class="d-flex pa-4 mb-4"
    >
      <!-- LEFT: TABLE -->
      <div style="width: 55%;">
        <div class="text-subtitle-1 font-weight-bold mb-2">{{ future.name }}</div>
        <div style="max-height: 330px; overflow-y: auto;">
          <v-data-table
            :headers="futuresDataHeaders"
            :items="formattedFutures(future.items)"
            item-key="name"
            :items-per-page="1000"
            hide-default-header
            hide-default-footer
            disable-sort
            height="330"
            fixed-header
          >
            <template v-slot:header="{ props }">
              <thead >
                <tr>
                  <th
                    v-for="header in props.headers"
                    :key="header.value"
                    class="text-center white--text"
                    style="background-color: #2a2a2a"
                  >
                    {{ header.text }}
                  </th>
                </tr>
              </thead>
            </template>
          </v-data-table>
        </div>
      </div>

      <!-- RIGHT: CHART -->
      <div
        class="d-flex flex-column align-center rounded-lg mt-9 ml-6"
        style="flex: 1; background-color: #121212; width: 45%;"
      >
        <div class="text-h5 font-weight-bold mt-4 mb-2">
          {{ future.name }}
        </div>
        <v-chart
          :option="chartOptions(future.items)"
          class="chart"
        ></v-chart>
      </div>
    </v-card>

    <!-- MARKET STATUS SECTION -->
     <div class="d-flex justify-end">
       <v-card
         class="d-flex flex-column rounded-lg pa-4"
         style="width: 33%;"
       >
         <div class="text-h5 font-weight-bold">Market Status</div>
         <v-list dense class="px-4" style="width: 100%;">
           <v-list-item>
             <v-list-item-content>Market:</v-list-item-content>
             <v-list-item-content class="text-right green--text font-weight-bold">Open</v-list-item-content>
           </v-list-item>
           <v-list-item>
             <v-list-item-content>Total Volume:</v-list-item-content>
             <v-list-item-content class="text-right">
               {{ getTotalVolume().toLocaleString('en-US') }}
             </v-list-item-content>
           </v-list-item>
           <v-list-item>
             <v-list-item-content>Active Contracts:</v-list-item-content>
             <v-list-item-content class="text-right">{{ activeContracts }}</v-list-item-content>
           </v-list-item>
           <v-list-item>
             <v-list-item-content>Update Interval:</v-list-item-content>
             <v-list-item-content class="text-right">5 seconds</v-list-item-content>
           </v-list-item>
         </v-list>
       </v-card>
     </div>
  </div>
</template>

<script>
import { mapActions } from "pinia"
import { useTableStore } from "../../store/curves"

import VChart from "vue-echarts"
import { use } from 'echarts/core'
import { LineChart } from 'echarts/charts'
import {
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent
} from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'

use([
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  LineChart,
  CanvasRenderer
])

export default {
  components: {
    VChart,
  },
  data () {
    return {
      futuresDataHeaders: [
        { text: this.$t('pricer.headers.symbol').toUpperCase(), value: "symbol", align: "center", sortable: false },
        { text: this.$t('pricer.headers.bid').toUpperCase(), value: "bid", align: "center", sortable: false },
        { text: this.$t('pricer.headers.ask').toUpperCase(), value: "ask", align: "center", sortable: false },
        { text: this.$t('pricer.headers.last').toUpperCase(), value: "last", align: "center", sortable: false },
        { text: this.$t('pricer.headers.high').toUpperCase(), value: "high", align: "center", sortable: false },
        { text: this.$t('pricer.headers.low').toUpperCase(), value: "low", align: "center", sortable: false },
        { text: this.$t('pricer.headers.change').toUpperCase(), value: "change", align: "center", sortable: false },
        { text: '% ' + this.$t('pricer.headers.change').toUpperCase(), value: "changePercent", align: "center", sortable: false },
        { text: this.$t('pricer.headers.previous').toUpperCase(), value: "previous", align: "center", sortable: false },
        { text: this.$t('pricer.headers.expiration').toUpperCase(), value: "expiration", align: "center", sortable: false },
        { text: this.$t('pricer.headers.vol').toUpperCase(), value: "vol", align: "center", sortable: false },
      ],
      di1Futures: [],
      frcFutures: [],
      wdoFutures: [],
      eurUsdFutures: [],
      totalVolume: null,
      activeContracts: null
    }
  },
  mounted () {
    this.fetchFuturesData()
  },
  computed: {
    futures () {
      return [
        { name: this.$t('pricer.di1'), items: this.di1Futures },
        { name: this.$t('pricer.frc'), items: this.frcFutures },
        { name: this.$t('pricer.wdo'), items: this.wdoFutures },
        { name: this.$t('pricer.eurusd'), items: this.eurUsdFutures }
      ]
    }
  },
  methods: {
    ...mapActions(useTableStore, [
      "getFuturesData"
    ]),
    fetchFuturesData () {
      this.getFuturesData(true)
        .then(response => {
          const data = response
          this.di1Futures = data.market_data.DI1
          this.frcFutures = data.market_data.FRC
          this.wdoFutures = data.market_data.WDO
          this.eurUsdFutures = data.market_data.EURUSD
          this.activeContracts = data.summary.total_contracts
        })
        .catch(error => {
          console.log(error)
        })
    },
    formattedFutures (futures) {
      return futures.map(item => ({
        symbol: item.symbol,
        bid: item.bid?.toFixed(3),
        ask: item.ask?.toFixed(3),
        last: item.last?.toFixed(3),
        high: item.high?.toFixed(3),
        low: item.low?.toFixed(3),
        change: this.formatChange(item.change),
        changePercent: this.formatChange(item.changePercent, true),
        previous: item.previous?.toFixed(3),
        expiration: item.expiration,
        vol: item.volume?.toLocaleString('en-US')
      }))
    },
    formatChange (value, isPercent = false) {
      const prefix = value > 0 ? '+' : value < 0 ? '−' : ''
      const absValue = Math.abs(value)
      const formatted = isPercent
        ? absValue.toFixed(2) + '%'
        : absValue.toFixed(3)
      return `${prefix}${formatted}`
    },
    getTotalVolume () {
      const all = [
        ...this.di1Futures,
        ...this.frcFutures,
        ...this.wdoFutures,
        ...this.eurUsdFutures
      ]
      this.totalVolume = all.reduce((sum, item) => sum + (item.volume || 0), 0)
      return this.totalVolume.toLocaleString('en-US')
    },
    chartOptions (futures) {
      const bidValues = futures.map(i => i.bid)
      const askValues = futures.map(i => i.ask)
      const allValues = [...bidValues, ...askValues]

      const minValue = Math.min(...allValues)
      const maxValue = Math.max(...allValues)
      const range = maxValue - minValue || 0.0001

      const interval = range / 4
      const adjustedMin = Math.floor(minValue / interval) * interval
      const adjustedMax = adjustedMin + interval * 4

      return {
        tooltip: {
          trigger: 'axis',
          formatter: (params) => {
            let tooltipText = params[0].axisValue + '<br/>'
            params.forEach(item => {
              tooltipText += `
                <span style="display:inline-block;margin-right:5px;border-radius:2px;width:10px;height:10px;background-color:${item.color};"></span>
                ${item.seriesName}: ${item.data.toFixed(3)}<br/>
              `
            })
            return tooltipText
          }
        },
        legend: {
          data: ["Bid", "Ask"],
          textStyle: {
            color: "#fff",
          },
          orient: "horizontal",
          top: "1%",
          left: "center",
          icon: "rect",
          itemWidth: 15,
          itemHeight: 10,
        },
        grid: {
          left: '10%',
          right: '10%',
          top: '15%',
          bottom: '15%'
        },
        xAxis: {
          type: 'category',
          data: futures.map((i) => i.expiration),
          axisLabel: { color: "#fff" },
          axisTick: { show: false },
          axisLine: { show: false }
        },
        yAxis: {
          type: 'value',
          min: adjustedMin,
          max: adjustedMax,
          interval: interval,
          axisLabel: {
            color: '#fff',
            formatter: value => value.toFixed(4)
          }
        },
        series: [
          {
            name: this.$t('pricer.headers.bid'),
            type: 'line',
            data: bidValues,
            smooth: true,
            itemStyle: { color: "#FFC107" }
          },
          {
            name: this.$t('pricer.headers.ask'),
            type: 'line',
            data: askValues,
            smooth: true,
            itemStyle: { color: "#FF6D00" }
          }
        ]
      }
    }
  }
}
</script>

<style scoped>
.chart {
  max-width: 800px;
  min-width: 600px;
  width: 100%;
  height: 300px
}
</style>